import React, {
	useCallback,
	useDeferredValue,
	useEffect,
	useState,
	useSyncExternalStore,
	type ReactNode,
} from 'react';
import { usePreloadedQuery, useRelayEnvironment } from 'react-relay';

import type { RelayError } from '@confluence/relay-errors/entry-points/RelayError.ts';
import { RelaySuspense } from '@confluence/query-preloader-tools/entry-points/RelaySuspense.ts';
import { CommentPlaceholder } from '@confluence/comment-simple/entry-points/CommentPlaceholder.ts';

import query, {
	type ObjectCommentContainerPreloadObjectCommentsQuery,
	type ObjectCommentContainerPreloadObjectCommentsQuery$data,
	type ObjectCommentContainerPreloadObjectCommentsQuery$variables,
} from './components/__generated__/ObjectCommentContainerPreloadObjectCommentsQuery.graphql.ts';
import {
	getConversationQueryOwner,
	subscribeConversationQueryOwner,
} from './ConversationQueryOwner.ts';
import type { ConversationQueryReference } from './ConversationQueryReference.ts';

type Props = {
	variables: ObjectCommentContainerPreloadObjectCommentsQuery$variables;
	fallback: ReactNode;
	/** Footer list membership is a snapshot; focused-inline data remains subscribed. */
	snapshot?: boolean;
	children: (
		data: ObjectCommentContainerPreloadObjectCommentsQuery$data | undefined,
		error?: RelayError,
	) => ReactNode;
};

/** Consumes only references prepared by the route/bootstrap; uncovered callers keep their reader. */
export function ConversationQueryReader(props: Props): React.JSX.Element {
	const environment = useRelayEnvironment();

	const subscribe = useCallback(
		(listener: () => void) => subscribeConversationQueryOwner(environment, listener),
		[environment],
	);
	const getSnapshot = useCallback(
		() => getConversationQueryOwner(environment)?.get(props.variables),
		[environment, props.variables],
	);
	// Replacements can keep the same variables; late streaming can also create the first owner.
	const requested = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
	// Defer same-operation handoffs so background adoption does not interrupt hydration or drafts.
	const deferred = useDeferredValue(requested);
	const reference =
		deferred && deferred.identifier !== requested?.identifier ? requested : deferred;
	useEffect(
		() => requested && getConversationQueryOwner(environment)?.retain(requested),
		[environment, requested],
	);
	useEffect(
		() => reference && getConversationQueryOwner(environment)?.retain(reference),
		[environment, reference],
	);

	return (
		<RelaySuspense
			interactionName="ConversationPreloadedQuery"
			fallback={<CommentPlaceholder showPlaceholder />}
		>
			{reference && !reference.disposed ? (
				<QueryReferenceReader reference={reference} snapshot={props.snapshot}>
					{props.children}
				</QueryReferenceReader>
			) : (
				props.fallback
			)}
		</RelaySuspense>
	);
}

function QueryReferenceReader(props: {
	reference: ConversationQueryReference;
	snapshot?: boolean;
	children: Props['children'];
}): React.JSX.Element {
	const { reference, snapshot } = props;
	const [lastSuccessful, setLastSuccessful] = useState<ConversationQueryReference>();
	if (snapshot && reference.settled && !reference.failed && lastSuccessful !== reference) {
		setLastSuccessful(reference);
	}
	useEffect(() => {
		if (lastSuccessful)
			return getConversationQueryOwner(lastSuccessful.environment)?.retain(lastSuccessful);
	}, [lastSuccessful]);

	// Recheck within Suspense when a pending execution retries after a failure.
	// Preserve a same-page footer's mounted draft when a refresh fails.
	const previous =
		snapshot &&
		lastSuccessful &&
		!lastSuccessful.disposed &&
		lastSuccessful.environment === reference.environment &&
		lastSuccessful.variables.pageId === reference.variables.pageId &&
		lastSuccessful.variables.includeConversationData ===
			reference.variables.includeConversationData &&
		lastSuccessful.variables.includeCommentThreads === reference.variables.includeCommentThreads
			? lastSuccessful
			: undefined;
	if (reference.failed && !previous) return <>{props.children(undefined, reference.error)}</>;
	return (
		<PreloadedConversation reference={reference.failed ? previous! : reference} snapshot={snapshot}>
			{(data, error) => props.children(data, reference.error ?? error)}
		</PreloadedConversation>
	);
}

function PreloadedConversation({
	reference,
	snapshot,
	children,
}: {
	reference: ConversationQueryReference;
	snapshot?: boolean;
	children: Props['children'];
}): React.JSX.Element {
	const data = usePreloadedQuery<ObjectCommentContainerPreloadObjectCommentsQuery>(
		query,
		reference.reference,
	);
	const [initial, setInitial] = useState(() => ({ reference, data }));
	// Match the old no-further-update reader: new owners refresh the snapshot, store writes do not.
	// Keep the same child position so query updates cannot remount a draft editor.
	if (initial.reference !== reference) setInitial({ reference, data });
	return (
		<>
			{children(snapshot && initial.reference === reference ? initial.data : data, reference.error)}
		</>
	);
}
